import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { node } from 'prop-types';
import { useDispatch } from 'react-redux';

import { noop } from '@ajaibid/utils';
import { API_SUCCESS_CODE } from '@config/constanta';
import { AlertContext } from '@context/alert';
import { onRequestPriceFromWatchlist } from 'Redux/detailStock';

import {
  getStockWatchlistGroup,
  getStockWatchlistMember,
  postSelectWatchlistGroup,
  postStockWatchlistGroup,
  putStockWatchlistGroup,
  delStockWatchlistGroup,
  putStockWatchlistSortOrder,
  postStockWatchlist,
  delStockWatchlist,
} from '@services/api/platform/watchlist/stock';
import {
  getMfWatchlistMember,
  postMfWatchlist,
  delMfWatchlist,
} from '@services/api/platform/watchlist/mutual-fund';

const INITIAL_CONTEXT_VALUE = {
  watchlistGroups: [],
  selectStockWatchlistGroup: (id) => {},
  createStockWatchlistGroup: (watchlistName) => {},
  updateStockWatchlistGroup: (id, watchlistName) => {},
  removeStockWatchlistGroup: (id) => {},
  sortStockWatchlistGroup: (id, payload) => {},
  isLoadingWatchlistStock: false,
  watchlistStock: null,
  addStockToWatchlist: (stockCode, onSuccess = noop) => {},
  removeStockFromWatchlist: (stockCode, onSuccess = noop) => {},
  isLoadingWatchlistMf: false,
  watchlistMf: null,
  addMfToWatchlist: (id, onSuccess = noop) => {},
  removeMfFromWatchlist: (id, onSuccess = noop) => {},
};

export const WatchlistContext = React.createContext(INITIAL_CONTEXT_VALUE);

const WatchlistProvider = ({ children }) => {
  const isMounted = useRef(true);
  const dispatch = useDispatch();
  const { showAlert } = useContext(AlertContext);

  const [watchlistGroups, setWatchlistGroups] = useState([]);
  const [watchlistStock, setWatchlistStock] = useState(null);
  const [isLoadingWatchlistStock, setIsLoadingWatchlistStock] = useState(false);

  const [watchlistMf, setWatchlistMf] = useState(null);
  const [isLoadingWatchlistMf, setIsLoadingWatchlistMf] = useState(false);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    watchlistStock?.group_members?.forEach((e) => {
      dispatch(onRequestPriceFromWatchlist(e, e.code));
    });
  }, [watchlistStock]);

  const fetchStockWatchlistGroups = useCallback(async () => {
    const response = await getStockWatchlistGroup();
    if (!isMounted.current) {
      return;
    }
    if (response.results) setWatchlistGroups(response.results);
  }, [getStockWatchlistGroup]);

  const getStockWatchlist = useCallback(async () => {
    setIsLoadingWatchlistStock(true);
    const response = await getStockWatchlistMember();
    if (!isMounted.current) {
      return;
    }
    if (response.result) setWatchlistStock(response.result);
    setIsLoadingWatchlistStock(false);
  }, [getStockWatchlistMember]);

  const selectStockWatchlistGroup = useCallback(
    async (id) => {
      await postSelectWatchlistGroup(id);
      getStockWatchlist();
    },
    [postSelectWatchlistGroup, getStockWatchlist]
  );

  const createStockWatchlistGroup = useCallback(
    async (watchlistName) => {
      const payload = { name: watchlistName };
      const resp = await postStockWatchlistGroup(payload);
      if (!isMounted.current) {
        return;
      }
      if (resp.err_code === API_SUCCESS_CODE) {
        showAlert(`Berhasil membuat Watchlist "${watchlistName}"`, 'success');
      } else {
        showAlert(`Gagal membuat Watchlist "${watchlistName}"`, 'error');
      }
      fetchStockWatchlistGroups();
    },
    [postStockWatchlistGroup, fetchStockWatchlistGroups]
  );

  const updateStockWatchlistGroup = useCallback(
    async (id, watchlistName) => {
      const payload = { id, name: watchlistName };
      const resp = await putStockWatchlistGroup(payload);
      if (!isMounted.current) {
        return;
      }
      if (resp.err_code === API_SUCCESS_CODE) {
        showAlert(`Berhasil mengubah Watchlist "${watchlistName}"`, 'success');
      } else {
        showAlert(`Gagal mengubah Watchlist "${watchlistName}"`, 'error');
      }
      fetchStockWatchlistGroups();
      getStockWatchlist();
    },
    [postStockWatchlistGroup, fetchStockWatchlistGroups]
  );

  const removeStockWatchlistGroup = useCallback(
    async (id, watchlistName) => {
      const resp = await delStockWatchlistGroup(id);
      if (!isMounted.current) {
        return;
      }
      if (resp.err_code === API_SUCCESS_CODE) {
        showAlert(`Berhasil mengubah Watchlist "${watchlistName}"`, 'success');
      } else {
        showAlert(`Gagal mengubah Watchlist "${watchlistName}"`, 'error');
      }
      fetchStockWatchlistGroups();
      getStockWatchlist();
    },
    [delStockWatchlistGroup, fetchStockWatchlistGroups]
  );

  const sortStockWatchlistGroup = useCallback(
    async (id, payload) => {
      await putStockWatchlistSortOrder(id, payload);
      getStockWatchlist();
    },
    [putStockWatchlistSortOrder, getStockWatchlist]
  );

  const addStockToWatchlist = useCallback(
    async (stockCode, onSuccess = noop) => {
      await postStockWatchlist(stockCode);
      getStockWatchlist();
      onSuccess();
    },
    [postStockWatchlist, getStockWatchlist]
  );

  const removeStockFromWatchlist = useCallback(
    async (stockCode, onSuccess = noop) => {
      await delStockWatchlist(stockCode);
      getStockWatchlist();
      onSuccess();
    },
    [delStockWatchlist, getStockWatchlist]
  );

  const getMfWatchlist = useCallback(async () => {
    setIsLoadingWatchlistMf(true);
    const response = await getMfWatchlistMember();
    if (response) {
      setWatchlistMf(response);
    }
    setIsLoadingWatchlistMf(false);
  }, [getMfWatchlistMember]);

  const addMfToWatchlist = useCallback(
    async (id, onSuccess = noop) => {
      await postMfWatchlist(id);
      getMfWatchlist();
      onSuccess();
    },
    [postMfWatchlist, getMfWatchlist]
  );

  const removeMfFromWatchlist = useCallback(
    async (id, onSuccess = noop) => {
      await delMfWatchlist(id);
      getMfWatchlist();
      onSuccess();
    },
    [delMfWatchlist, getMfWatchlist]
  );

  useEffect(() => {
    if (!watchlistStock) {
      getStockWatchlist();
      fetchStockWatchlistGroups();
    }
  }, [getStockWatchlist]);

  useEffect(() => {
    getMfWatchlist();
  }, [getMfWatchlist]);

  const valueProps = useMemo(
    () => ({
      watchlistGroups,
      selectStockWatchlistGroup,
      createStockWatchlistGroup,
      updateStockWatchlistGroup,
      removeStockWatchlistGroup,
      sortStockWatchlistGroup,
      isLoadingWatchlistStock,
      watchlistStock,
      addStockToWatchlist,
      removeStockFromWatchlist,
      isLoadingWatchlistMf,
      watchlistMf,
      addMfToWatchlist,
      removeMfFromWatchlist,
    }),
    [
      watchlistGroups,
      selectStockWatchlistGroup,
      createStockWatchlistGroup,
      updateStockWatchlistGroup,
      removeStockWatchlistGroup,
      sortStockWatchlistGroup,
      isLoadingWatchlistStock,
      watchlistStock,
      addStockToWatchlist,
      removeStockFromWatchlist,
      isLoadingWatchlistMf,
      watchlistMf,
      addMfToWatchlist,
      removeMfFromWatchlist,
    ]
  );

  return <WatchlistContext.Provider value={valueProps}>{children}</WatchlistContext.Provider>;
};

WatchlistProvider.propTypes = {
  children: node.isRequired,
};

export default WatchlistProvider;
